import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { useEffect, useRef, useState } from "react";
import { CloseOutlined, PlusOutlined, SendOutlined } from "@ant-design/icons";
import { Bubble, Prompts, Sender, Welcome, XProvider } from "@ant-design/x";
import { Button, Drawer, Flex, GetRef, Spin, Tag, theme } from "antd";
import { XMarkdown } from "@ant-design/x-markdown";
import clsx from "clsx";

import { useCopilot } from "../../lib/contexts/copilot";
import { createAgent, runAgent, useCopilotChat } from "./agent";
import { Heading2, Heading3, Heading4, withBottomMargin } from "../../styles/typography";
import { useOrganization } from "../../lib/contexts/organization";
import { AuthService } from "../../lib/auth";
import { addIf } from "../../lib/util";
import { SparklesIcon } from "../../lib/ui-components/layout/icons";

const markdownComponents: React.ComponentProps<typeof XMarkdown>["components"] = {
  h1: withBottomMargin(Heading2),
  h2: withBottomMargin(Heading3),
  h3: withBottomMargin(Heading4),
  h4: withBottomMargin(Heading4),
};

function MarkdownMessage({ content }: { content: string }) {
  return (
    <XMarkdown
      content={content}
      streaming={{ enableAnimation: true }}
      components={markdownComponents}
    />
  );
}

const roles: React.ComponentProps<typeof Bubble.List>["role"] = {
  user: { placement: "end" as const, styles: { root: { paddingInline: 0 } } },
  ai: {
    placement: "start" as const,
    contentRender: (content: string) => {
      return <MarkdownMessage content={content} />;
    },
    variant: "borderless",
    styles: {
      root: {
        paddingInline: 0,
      },
    },
  },
};

export function CopilotSidebar() {
  const senderRef = useRef<GetRef<typeof Sender>>(null);
  const { open, closeCopilot } = useCopilot();
  const { activeOrganization } = useOrganization();
  const activeOrgaId = activeOrganization!.id;
  const [agent, setAgent] = useState(() => createAgent(AuthService.getTokenSilently, activeOrgaId));
  const agentOrgaIdRef = useRef(activeOrgaId);

  const [isResized, setResized] = useState(false);

  useEffect(() => {
    if (!open) {
      setResized(false);
    }
  }, [open]);

  useEffect(() => {
    if (agentOrgaIdRef.current === activeOrgaId) return;
    agentOrgaIdRef.current = activeOrgaId;
    setAgent(createAgent(AuthService.getTokenSilently, activeOrgaId));
  }, [activeOrgaId]);

  const { messages, isRunning, isStreaming, isCallingTool } = useCopilotChat(agent);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const starterPrompts = [
    t`How can I reduce my peak load?`,
    t`Help me identify SEUs.`,
    t`Which measures can I take to save energy costs?`,
    t`Analyze my CO₂ emissions and suggest reduction measures`,
  ];

  function submit(value: string) {
    const text = value.trim();
    setErrorMessage(null);
    if (!text) return;

    void runAgent(agent, text, {
      onError() {
        setErrorMessage(t`⚠️ Something went wrong. Please try again later.`);
      },
    });
  }

  function startNewSession() {
    setAgent(createAgent(AuthService.getTokenSilently, activeOrgaId));
    setErrorMessage(null);
  }

  const { token } = theme.useToken();
  return (
    <XProvider
      theme={{
        components: {
          Sender: {
            colorBgActionsDisabled: token.colorBgContainerDisabled,
          },
        },
      }}
    >
      <div
        className={clsx("relative  max-w-full", {
          "duration-200 transition-[width]": open,
          "w-96": open && !isResized,
          "w-0": !open,
        })}
      >
        <Drawer
          open={open}
          destroyOnHidden
          classNames={{
            root: "static h-full",
            wrapper: "!static h-full min-w-96",
            body: "p-6",
          }}
          placement="right"
          closable={false}
          keyboard={false}
          getContainer={false}
          mask={false}
          resizable={{
            onResize: () => setResized(true),
          }}
          maxSize={960}
          title={
            <div className="flex gap-2 items-center">
              <SparklesIcon className="h-7 w-7 svg-magenta pr-2" />
              <Heading4>
                <Trans>AI Assistant</Trans>
              </Heading4>
              <Tag className="bg-magenta-50 text-magenta-600 border-magenta-600">BETA</Tag>
            </div>
          }
          extra={
            <>
              <Button
                type="text"
                icon={<PlusOutlined />}
                title={t`New session`}
                aria-label={t`New session`}
                onClick={startNewSession}
              />
              <Button
                type="text"
                icon={<CloseOutlined />}
                title={t`Close`}
                aria-label={t`Close`}
                onClick={() => closeCopilot()}
              />
            </>
          }
        >
          <div className="h-full flex flex-col gap-6">
            <div className="flex-1 overflow-y-auto">
              <div className="h-full flex flex-col">
                {messages.length === 0 && !errorMessage && (
                  <>
                    <Welcome
                      className="mb-4"
                      title={t`Hello, I am your AI assistant`}
                      description={t`I can access and analyze data from your current organization. I have no write access and can't modify anything.`}
                    />
                    <Prompts
                      vertical
                      items={starterPrompts.map((prompt) => ({ key: prompt, description: prompt }))}
                      onItemClick={(info) => submit(info.data.key ?? "")}
                    />
                  </>
                )}
                {messages.length > 0 && (
                  <Bubble.List
                    role={roles}
                    items={[
                      ...messages,
                      ...addIf(isRunning && !(isCallingTool || isStreaming), [
                        {
                          content: "",
                          loading: true,
                          loadingRender: () => (
                            <Flex align="center" gap="small">
                              <Spin size="small" />
                              <Trans>Thinking</Trans>
                            </Flex>
                          ),
                          role: "ai",
                          key: "",
                        },
                      ]),
                      ...addIf(isCallingTool, [
                        {
                          content: "",
                          loading: true,
                          loadingRender: () => (
                            <Flex align="center" gap="small">
                              <Spin size="small" />
                              <Trans>Gather data</Trans>
                            </Flex>
                          ),
                          role: "ai",
                          key: "",
                        },
                      ]),
                    ]}
                  />
                )}
                {errorMessage && <Bubble.System content={errorMessage} />}
              </div>
            </div>

            <div className="flex-none">
              <Sender
                ref={senderRef}
                classNames={{
                  input: "focus:outline-none",
                }}
                onSubmit={(message) => {
                  submit(message);
                  senderRef.current?.clear();
                }}
                placeholder={t`Ask the assistant…`}
                suffix={(_, { components: { SendButton } }) => {
                  return (
                    <SendButton
                      type="primary"
                      icon={<SendOutlined />}
                      aria-label={t`Send`}
                      // @ts-expect-error We need to override the variable locally,
                      // otherwise the text color changes for disabled and enabled state,
                      // but we only want to change it for the disabled state.
                      style={{ "--ant-color-text-light-solid": token.colorTextDisabled }}
                    />
                  );
                }}
              />
              <div className="mt-2 block text-center text-xs text-secondary">
                <Trans>The AI can make mistakes. Please review its responses.</Trans>
              </div>
            </div>
          </div>
        </Drawer>
      </div>
    </XProvider>
  );
}
